/**
 * Pure Momentum Oscillator
 *
 * Volatility-normalised momentum indicator that isolates pure directional
 * velocity, plus a mirrored synthetic price envelope drawn on the price pane.
 *
 * Why not a plain ROC
 *   Raw price changes and plain percentage differences get distorted during
 *   volatility expansions and thin liquidity. Dividing by ATR keeps the reading
 *   comparable across volatility regimes and asset classes.
 *
 * Double-smoothed price projection engine
 *   De-trended smoothing — take the EMA of each OHLC leg (oa, ha, la, ca) and
 *   project `2 x Price - EMA`, which cancels most of the lag an EMA carries:
 *
 *       o = 2 x open  - ema(open,  length)
 *       h = 2 x high  - ema(high,  length)
 *       l = 2 x low   - ema(low,   length)
 *       c = 2 x close - ema(close, length)
 *
 *   Weighted secondary smoothing — smooth those projections with a Weighted
 *   Moving Average of the same lookback (oda, hda, lda, cda). The result is a
 *   clean synthetic price profile that tracks price without the bar noise.
 *
 * Volatility-normalised rate of change
 *       change = (cda - cda[1]) / atr(length)
 *
 * Threshold-based trend logic (latched state)
 *   Long  ( 1): change > longThreshold   → bullish momentum
 *   Short (-1): change < shortThreshold  → bearish momentum
 *   Neutral( 0): in between. The state latches, so a bullish run stays green
 *   until momentum actually breaks the lower threshold.
 *
 * Chart output
 *   • oda / hda / lda / cda drawn on the price pane as the smoothed envelope,
 *     recoloured by the current state (adaptive candle morphing).
 *   • The original price candles are recoloured to match.
 *   • The oscillator lives in its own sub-pane: a momentum line with a
 *     zero-based fill (green above, red below) plus the two threshold lines.
 *
 * Category: Trend (listed under the "Strategies" group) | Pane: Overlay + Sub
 */

import {
  IndicatorCurveBand,
  IndicatorDefinition,
  PlotData,
  PlotPoint,
} from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";
import { computeATR } from "./atr";
import { withAlpha } from "../color-utils";

// ─── State constants ─────────────────────────────────────────────────────────
const STATE_NEUTRAL = 0;
const STATE_LONG = 1;
const STATE_SHORT = -1;

export const pureMomentumOscillatorIndicator: IndicatorDefinition = {
  id: "pure_momentum_oscillator",
  name: "Pure Momentum Oscillator",
  shortName: "PMO",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "length", label: "Lookback", type: "number", default: 45, min: 2, max: 500, group: "Momentum" },
    { key: "longThreshold", label: "Long Threshold", type: "number", default: 0.05, step: 0.01 },
    { key: "shortThreshold", label: "Short Threshold", type: "number", default: -0.05, step: 0.01 },

    { key: "bullColor", label: "Bullish Color", type: "color", default: "#26D07C", group: "Colors" },
    { key: "bearColor", label: "Bearish Color", type: "color", default: "#F23645" },
    { key: "neutralColor", label: "Neutral Color", type: "color", default: "#787878" },

    { key: "recolorCandles", label: "Recolor Candles", type: "boolean", default: true, group: "Display" },
    { key: "showEnvelope", label: "Show Price Envelope", type: "boolean", default: true },
    { key: "showEnvelopeBands", label: "Envelope O/H/L", type: "boolean", default: true },
    { key: "smoothCloseWidth", label: "Envelope Width", type: "number", default: 3, min: 1, max: 6, step: 1 },
    { key: "showOscillator", label: "Show Oscillator", type: "boolean", default: true },
    { key: "showFill", label: "Show Momentum Fill", type: "boolean", default: true },
    { key: "fillOpacity", label: "Fill Opacity %", type: "number", default: 30, min: 5, max: 80, step: 5 },
    { key: "showThresholds", label: "Show Threshold Lines", type: "boolean", default: true },
  ],
  plots: [
    // ── Price pane: double-smoothed synthetic OHLC ──
    { key: "smoothClose", label: "Smoothed Close", color: "#26D07C", style: "line", lineWidth: 3, lastValueVisible: true, priceLineVisible: false },
    { key: "smoothHigh", label: "Smoothed High", color: "#26D07C", style: "line", lineWidth: 1, lastValueVisible: false, priceLineVisible: false },
    { key: "smoothLow", label: "Smoothed Low", color: "#26D07C", style: "line", lineWidth: 1, lastValueVisible: false, priceLineVisible: false },
    { key: "smoothOpen", label: "Smoothed Open", color: "#26D07C", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false, priceLineVisible: false },

    // ── Sub-pane: the oscillator ──
    { key: "momentum", label: "Momentum", color: "#26D07C", style: "line", lineWidth: 2, pane: "separate", lastValueVisible: true, priceLineVisible: false },
    { key: "longThreshold", label: "Long Threshold", color: "#26D07C", style: "line", lineWidth: 1, pane: "separate", lastValueVisible: false, priceLineVisible: false },
    { key: "shortThreshold", label: "Short Threshold", color: "#F23645", style: "line", lineWidth: 1, pane: "separate", lastValueVisible: false, priceLineVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const len = candles.length;
    const length = Math.max(2, Math.round(params.length ?? 45));
    const longThreshold = Number.isFinite(Number(params.longThreshold)) ? Number(params.longThreshold) : 0.05;
    const shortThreshold = Number.isFinite(Number(params.shortThreshold)) ? Number(params.shortThreshold) : -0.05;

    const bullColor = params.bullColor ?? "#26D07C";
    const bearColor = params.bearColor ?? "#F23645";
    const neutralColor = params.neutralColor ?? "#787878";

    const recolorCandles = params.recolorCandles !== false;
    const showEnvelope = params.showEnvelope !== false;
    const showEnvelopeBands = params.showEnvelopeBands !== false;
    const envelopeWidth = Math.max(1, Math.min(6, Math.round(params.smoothCloseWidth ?? 3)));
    const showOscillator = params.showOscillator !== false;
    const showFill = params.showFill !== false;
    const fillOpacity = Math.max(0, Math.min(100, params.fillOpacity ?? 30)) / 100;
    const showThresholds = params.showThresholds !== false;

    // EMA needs `length` bars and WMA needs `length` more, so the smoothed
    // stream starts at 2 x length - 1 and momentum one bar after that.
    const firstMomentum = 2 * length;
    if (len <= firstMomentum) {
      return [
        { key: "smoothClose", data: [] },
        { key: "smoothHigh", data: [] },
        { key: "smoothLow", data: [] },
        { key: "smoothOpen", data: [] },
        { key: "momentum", data: [] },
        { key: "longThreshold", data: [] },
        { key: "shortThreshold", data: [] },
      ];
    }

    // ── 1. De-trended smoothing: 2 x Price - EMA ────────────────────────────
    const highs = candles.map(c => c.high);
    const lows = candles.map(c => c.low);
    const opens = candles.map(c => c.open);
    const closes = candles.map(c => c.close);

    const emaOpen = computeEMA(opens, length);
    const emaHigh = computeEMA(highs, length);
    const emaLow = computeEMA(lows, length);
    const emaClose = computeEMA(closes, length);

    const projOpen = candles.map((c, i) => 2 * c.open - emaOpen[i]);
    const projHigh = candles.map((c, i) => 2 * c.high - emaHigh[i]);
    const projLow = candles.map((c, i) => 2 * c.low - emaLow[i]);
    const projClose = candles.map((c, i) => 2 * c.close - emaClose[i]);

    // ── 2. Weighted secondary smoothing ─────────────────────────────────────
    const smoothOpen = computeWMA(projOpen, length);
    const smoothHigh = computeWMA(projHigh, length);
    const smoothLow = computeWMA(projLow, length);
    const smoothClose = computeWMA(projClose, length);

    // ── 3. Volatility-normalised rate of change ────────────────────────────
    const atr = computeATR(candles, length);
    const change: number[] = new Array(len).fill(NaN);
    for (let i = firstMomentum; i < len; i++) {
      const a = atr[i];
      if (!isFinite(a) || a <= 0) continue;
      const delta = smoothClose[i] - smoothClose[i - 1];
      if (!isFinite(delta)) continue;
      change[i] = delta / a;
    }

    // ── 4. Latched threshold state ──────────────────────────────────────────
    const state: number[] = new Array(len).fill(STATE_NEUTRAL);
    let current = STATE_NEUTRAL;
    for (let i = firstMomentum; i < len; i++) {
      const m = change[i];
      if (!isFinite(m)) continue;
      if (m > longThreshold) current = STATE_LONG;
      else if (m < shortThreshold) current = STATE_SHORT;
      state[i] = current;
    }

    const colorFor = (i: number): string =>
      state[i] === STATE_LONG ? bullColor : state[i] === STATE_SHORT ? bearColor : neutralColor;

    // ── 5. Plot series ──────────────────────────────────────────────────────
    const smoothCloseData: PlotPoint[] = [];
    const smoothHighData: PlotPoint[] = [];
    const smoothLowData: PlotPoint[] = [];
    const smoothOpenData: PlotPoint[] = [];
    const momentumData: PlotPoint[] = [];
    const longLineData: PlotPoint[] = [];
    const shortLineData: PlotPoint[] = [];

    // The envelope mirrors the smoothed stream (starts one bar before momentum).
    for (let i = 2 * length - 1; i < len; i++) {
      const t = candles[i].time;
      const c = colorFor(i);
      if (showEnvelope) {
        smoothCloseData.push({ time: t, value: smoothClose[i], color: c });
      }
      if (showEnvelope && showEnvelopeBands) {
        smoothHighData.push({ time: t, value: smoothHigh[i], color: c });
        smoothLowData.push({ time: t, value: smoothLow[i], color: c });
        smoothOpenData.push({ time: t, value: smoothOpen[i], color: c });
      }
    }

    for (let i = firstMomentum; i < len; i++) {
      const m = change[i];
      if (!isFinite(m)) continue;
      const t = candles[i].time;
      const c = state[i] === STATE_LONG ? bullColor : state[i] === STATE_SHORT ? bearColor : neutralColor;
      if (showOscillator) momentumData.push({ time: t, value: m, color: c });
      if (showThresholds) {
        longLineData.push({ time: t, value: longThreshold, color: withAlpha(bullColor, 0.6) });
        shortLineData.push({ time: t, value: shortThreshold, color: withAlpha(bearColor, 0.6) });
      }
    }

    // ── 6. Zero-based momentum fill (green above, red below) ────────────────
    const curveBands: IndicatorCurveBand[] = [];
    if (showOscillator && showFill && momentumData.length > 1) {
      const upper = momentumData.map(p => ({ time: p.time, value: p.value }));
      const lower = upper.map(p => ({ time: p.time, value: 0 }));

      const barStep = len >= 2 ? Math.max(1, candles[1].time - candles[0].time) : 0;
      const fillSegments: NonNullable<IndicatorCurveBand["segments"]> = [];
      let runStart = 0;
      for (let k = 1; k <= momentumData.length; k++) {
        const prevSign = Math.sign(momentumData[runStart].value) || 1;
        const sign = k < momentumData.length ? Math.sign(momentumData[k].value) || prevSign : -2;
        if (sign === prevSign) continue;
        const endIdx = k - 1;
        fillSegments.push({
          fromTime: momentumData[runStart].time,
          toTime: endIdx + 1 < momentumData.length
            ? momentumData[endIdx + 1].time
            : momentumData[endIdx].time + barStep,
          color: withAlpha(prevSign > 0 ? bullColor : bearColor, fillOpacity),
        });
        runStart = k;
      }

      curveBands.push({
        id: "pmo-fill",
        hostPlotKey: "momentum",
        upper,
        lower,
        segments: fillSegments,
        borderWidth: 0,
        fill: true,
      });
    }

    // ── 7. Adaptive candle morphing ─────────────────────────────────────────
    const candleColors: { time: number; color: string }[] = [];
    if (recolorCandles) {
      for (let i = firstMomentum; i < len; i++) {
        candleColors.push({ time: candles[i].time, color: colorFor(i) });
      }
    }

    return [
      {
        key: "smoothClose",
        data: smoothCloseData,
        curveBands,
        // `envelopeWidth` is picked up by the renderer via the
        // `<plotKey>Width` param convention — echoed here for completeness.
        metadata: { candleColors, momentum: momentumData, envelopeWidth },
      },
      { key: "smoothHigh", data: smoothHighData },
      { key: "smoothLow", data: smoothLowData },
      { key: "smoothOpen", data: smoothOpenData },
      { key: "momentum", data: momentumData },
      { key: "longThreshold", data: longLineData },
      { key: "shortThreshold", data: shortLineData },
    ];
  },
};

/**
 * Weighted Moving Average — linearly weighted toward the most recent value:
 *   wma[i] = Σ (length - k) x[i-k] / Σ (length - k),  k = 0 .. length-1
 * Aligned 1:1 with the input; NaN until the first full window.
 */
function computeWMA(values: number[], length: number): number[] {
  const n = values.length;
  const out: number[] = new Array(n).fill(NaN);
  const denom = (length * (length + 1)) / 2;
  for (let i = length - 1; i < n; i++) {
    let sum = 0;
    for (let k = 0; k < length; k++) sum += (length - k) * values[i - k];
    out[i] = sum / denom;
  }
  return out;
}